import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — lightweight, dependency-free scroll-entrance animation.
 *
 * Fades and eases its children into view the first time they enter the
 * viewport. Honours `prefers-reduced-motion` (handled in index.css).
 *
 * Props
 *  - as:     element/component to render (default "div")
 *  - delay:  ms before the transition starts (use for staggering grids)
 *  - y:      px to travel upward into place (default 28)
 *  - once:   only animate the first time (default true)
 */
const Reveal = ({
  children,
  as: Tag = "div",
  delay = 0,
  y = 28,
  once = true,
  className = "",
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once]);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "in-view" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms`, "--reveal-y": `${y}px` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
